import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import colors from 'common/styles/colors';
import { Table } from 'semantic-ui-react';
import * as _ from 'lodash';
import NewBatteryIcon from 'common/images/NewBatteryIcon';
import MoteUnreachableNew from 'common/images/MoteUnreachableNew';
import { temperatureUnitSystemMap, METRIC_UNIT_SYSTEM } from 'common/constants';

const H3 = styled.span`
  font-size: 12px;
  font-family: Petasense Open Sans;
  display: block;
  text-align: center; 
`;

const TEMPERATURE_LIMITS = {
  GREEN_LOWER: 60,
  GREEN_UPPER: 90,
  YELLOW_LOWER: 45,
  YELLOW_UPPER: 100
};

const StyledTableCell = styled(Table.Cell)`
  &.warning-cell {
    background-color: ${colors.battery_level_warning};
  }
  &.good-cell {
    background-color:  ${colors.default};
  }
  &.critical-cell {
    background-color: ${colors.alarmCritical};
  }
  font-weight: 800;
  color: black;
`;

const ToolTipContainer = styled.span`
  &:before {
    display: none;
  }
  &:after {
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 4;
    transform: none !important;
    transition-property: none !important;
    animation: none !important;
    -webkit-transform-origin: unset;
    transform-origin: unset;
    background: #000000 !important;
    color: #EEEEEE !important;
    padding: 5px !important;
    font-size: 12px !important;
  }
`;

const getTemperatureCellProps = (value, isUnreachable, limits) => {
  if (isUnreachable || value === null || Number.isNaN(value)) {
    return { isGood: false, isWarning: false, isCritical: false };
  }
  return {
    isGood: value >= limits.GREEN_LOWER && value <= limits.GREEN_UPPER,
    isWarning: value >= limits.YELLOW_LOWER && value <= limits.YELLOW_UPPER,
    isCritical: value < limits.YELLOW_LOWER || value > limits.YELLOW_UPPER
  };
};

const getCell = (item, feature_key, dashboardConfig, temperatureUnit) => {
  const value = item[feature_key] !== undefined && item[feature_key] !== null ? _.round(item[feature_key], 2) : '--';
  const temperatureLimits = getTemperatureLimits(dashboardConfig, item.location_id);
  const { isGood, isWarning, isCritical } = getTemperatureCellProps(item[feature_key], item.unreachable, temperatureLimits);

  let className = '';
  if (isGood) {
    className = 'good-cell';
  } else if (isCritical) {
    className = 'critical-cell';
  } else if (isWarning) {
    className = 'warning-cell';
  }

  return (
    <StyledTableCell className={className}>
      <H3>
        <ToolTipContainer data-tooltip={value !== '--' ? `${value} ${temperatureUnit}` : '--'} data-position="left">
          {value}
        </ToolTipContainer>
      </H3>
    </StyledTableCell>
  );
};

const getTemperatureLimits = (dashboardConfig, location_id) => {
  const parseLimit = (val, defaultVal) => (!Number.isNaN(parseFloat(val)) ? parseFloat(val) : defaultVal);
  if (!dashboardConfig || dashboardConfig.type !== 'temperature') {
    return TEMPERATURE_LIMITS;
  }

  let limits = {
    GREEN_LOWER: parseLimit(dashboardConfig.green_lower_limit, TEMPERATURE_LIMITS.GREEN_LOWER),
    GREEN_UPPER: parseLimit(dashboardConfig.green_upper_limit, TEMPERATURE_LIMITS.GREEN_UPPER),
    YELLOW_LOWER: parseLimit(dashboardConfig.yellow_lower_limit, TEMPERATURE_LIMITS.YELLOW_LOWER),
    YELLOW_UPPER: parseLimit(dashboardConfig.yellow_upper_limit, TEMPERATURE_LIMITS.YELLOW_UPPER)
  };

  if (dashboardConfig.location_based_limits && Object.prototype.hasOwnProperty.call(dashboardConfig.location_based_limits, location_id)) {
    const locationLimits = dashboardConfig.location_based_limits[location_id];
    limits = {
      GREEN_LOWER: parseLimit(locationLimits.green_lower_limit, limits.GREEN_LOWER),
      GREEN_UPPER: parseLimit(locationLimits.green_upper_limit, limits.GREEN_UPPER),
      YELLOW_LOWER: parseLimit(locationLimits.yellow_lower_limit, limits.YELLOW_LOWER),
      YELLOW_UPPER: parseLimit(locationLimits.yellow_upper_limit, limits.YELLOW_UPPER)
    };
  }

  return limits;
};


const AreaTemperatureTable = ({ data, dashboardConfig, currentUser }) => {
  const { t } = useTranslation();
  const unitSystem = (currentUser && currentUser.unit_system) || METRIC_UNIT_SYSTEM;
  const temperatureUnit = temperatureUnitSystemMap[unitSystem].units;
  if (data.length === 0) {
    return (
      <div
        style={{
          borderRadius: '4px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: '1em', fontWeight: 'bold', color: 'gray' }}>--</div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: '100%',
        borderRadius: '4px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <div>
        <Table celled unstackable compact="very">
          <tbody>
            {data.map((area, index) => (
              <tr key={index} style={{ fontSize: '0.9em' }}>
                <td>
                  <Link
                    className="ps-hover-pointer"
                    to={{ pathname: '/configs', search: `?tag_id=${area.tag_id}` }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {area.name}
                      {area.low_battery && (
                        <ToolTipContainer data-tooltip={t('widgetLibrary.areaTemperature.lowBattery')} data-position="left">
                          <NewBatteryIcon fill={colors.red} />
                        </ToolTipContainer>
                      )}
                      {area.unreachable && (
                        <ToolTipContainer data-tooltip={t('widgetLibrary.areaTemperature.unreachable')} data-position="left">
                          <MoteUnreachableNew fill={colors.red} width={15} height={15} />
                        </ToolTipContainer>
                      )}
                    </div>
                  </Link>
                </td>
                {getCell(area, 'last_temperature', dashboardConfig, temperatureUnit)}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.user,
  dashboardConfig: state.dashboards.activeDashboard.dashboard_config
});

export default connect(
  mapStateToProps
)(AreaTemperatureTable);
